// V2 Knowledge Base Viewer — AVI-79. Read-only index of all assets crawled or
// uploaded for the agent context (website pages, articles, hosted pages).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconArrowLeft, IconFileText, IconLayout, IconPlus, IconWorld } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { fetchKnowledgeBaseAssets } from '../services/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const SOURCE_CONFIG = {
  website: { label: 'Website Pages', Icon: IconWorld },
  article: { label: 'Context Articles', Icon: IconFileText },
  hostedPage: { label: 'Hosted Pages', Icon: IconLayout },
};

// ─── Asset row ───────────────────────────────────────────────────────────────

function AssetRow({ asset }) {
  const { source, title, url, lastChangedAt, retiredAt } = asset;
  const { Icon } = SOURCE_CONFIG[source] || SOURCE_CONFIG.article;
  const displayTitle = (title && title.trim()) ? title : url;
  const dateStr = formatDate(lastChangedAt);

  return (
    <Paper
      p="sm"
      radius="sm"
      withBorder
      style={{ background: '#fff' }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Icon size={16} style={{ color: 'var(--aviary-muted)', marginTop: 2, flexShrink: 0 }} />
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" align="center" wrap="nowrap">
            <Text
              size="sm"
              fw={500}
              style={{ color: 'var(--aviary-ink)', flex: 1, minWidth: 0 }}
              truncate
            >
              {displayTitle}
            </Text>
            {retiredAt && (
              <Badge color="red" size="xs" variant="filled" style={{ flexShrink: 0 }}>
                Retired
              </Badge>
            )}
          </Group>
          {dateStr && (
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
              Last indexed {dateStr}
            </Text>
          )}
        </Stack>
      </Group>
    </Paper>
  );
}

// ─── Accordion panel ─────────────────────────────────────────────────────────

function SourcePanel({ source, assets }) {
  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.article;
  return (
    <Accordion.Item value={source}>
      <Accordion.Control>
        <Group gap="sm" align="center">
          <Text fw={600} style={{ color: 'var(--aviary-ink)' }}>
            {config.label}
          </Text>
          <Badge color="sage" size="sm" variant="light">
            {assets.length}
          </Badge>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          {assets.map((a) => (
            <AssetRow key={a.id} asset={a} />
          ))}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AviaryV2KnowledgeBasePage() {
  const api = useApi();
  const navigate = useNavigate();
  const [assets, setAssets] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchKnowledgeBaseAssets(api)
      .then((list) => { if (!cancelled) setAssets(list); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); });
    return () => { cancelled = true; };
  }, [api]);

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load knowledge base</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }

  if (assets === null) {
    return (
      <Center py={80}>
        <Loader color="sage" />
      </Center>
    );
  }

  // KB = website pages (all) + context-role articles/hostedPages only.
  // Promo articles belong in Articles, not here.
  const isContext = (a) => String(a.metadata?.articleRole || '').toLowerCase() === 'context';
  const buckets = {
    website: assets.filter((a) => a.source === 'website'),
    article: assets.filter((a) => a.source === 'article' && isContext(a)),
    hostedPage: assets.filter((a) => a.source === 'hostedPage' && isContext(a)),
  };

  const hasAny = Object.values(buckets).some((list) => list.length > 0);

  return (
    <div style={{ padding: '40px 24px 120px', maxWidth: 900, margin: '0 auto' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Group gap="sm" align="center">
            <Button
              variant="subtle"
              color="gray"
              size="sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/v2/articles')}
              px={6}
            >
              Articles
            </Button>
            <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
              Knowledge Base
            </Text>
          </Group>
          <Button
            leftSection={<IconPlus size={14} />}
            color="sage"
            variant="light"
            onClick={() => navigate('/v2/articles/new', { state: { defaultRole: 'context', back: { path: '/v2/knowledge-base', label: 'Knowledge Base' } } })}
          >
            New entry
          </Button>
        </Group>

        {!hasAny ? (
          <Center py={60}>
            <Text style={{ color: 'var(--aviary-muted)' }}>No assets indexed yet.</Text>
          </Center>
        ) : (
          <Paper radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
            <Accordion
              multiple
              defaultValue={Object.keys(buckets).filter((k) => buckets[k].length > 0)}
            >
              {Object.entries(buckets)
                .filter(([, list]) => list.length > 0)
                .map(([source, list]) => (
                  <SourcePanel key={source} source={source} assets={list} />
                ))}
            </Accordion>
          </Paper>
        )}
      </Stack>
    </div>
  );
}
